import React, { useEffect, useRef, useState } from 'react';
import { App, Button, Card, Input, Modal, Popconfirm, Space, Table, Tag, Typography } from 'antd';
import { hataMesaji, markaApi } from './markaApi';
import Yuva from './Yuva';

const { Text, Paragraph } = Typography;

export const YONELIM_ADI = { yatay: 'Yatay', 'yatay-degrade': 'Yatay (degrade)', 'yatay-atm': 'Yatay (ATM)', dikey: 'Dikey', amblem: 'Amblem', kompakt: 'Kompakt' };
const VARYANT_ADI = { renkli: 'Renkli', siyah: 'Siyah', beyaz: 'Beyaz (ters)', gri: 'Gri tonlama', 'tek-renk': 'Tek renk' };

/** Logo ekran verisini (backend'in işlediği iç SVG) çizer. */
function LogoSvg({ logo, yukseklik = 72, kutuRef, onClick, zemin }) {
  return (
    // Orta gri zemin: beyaz parçalı logolar (ör. CityChannel kompakt) da görünsün
    <div ref={kutuRef} onClick={onClick} style={{ background: zemin || '#d5d8e0', borderRadius: 8, padding: 12, textAlign: 'center' }}>
      <svg viewBox={logo.viewbox} style={{ height: yukseklik, maxWidth: '100%' }}
        // ic_svg backend'de beyaz listeyle denetlenip normalleştirildi (services/marka_portal_svg.py)
        dangerouslySetInnerHTML={{ __html: logo.ic_svg }} />
    </div>
  );
}

/** Amblem parçalarını tıklayarak seçme: koyu = amblem (symbol), soluk = yazı (wordmark). */
function AmblemPenceresi({ slug, logo, acik, onKapat, onDegisti }) {
  const { message } = App.useApp();
  const [secili, setSecili] = useState(new Set(logo.amblem_parcalar));
  const [mesgul, setMesgul] = useState(false);
  const kutu = useRef(null);

  useEffect(() => { if (acik) setSecili(new Set(logo.amblem_parcalar)); }, [acik, logo]);
  useEffect(() => {
    kutu.current?.querySelectorAll('.lp').forEach((el) => {
      const i = Number(el.style.getPropertyValue('--i'));
      el.style.opacity = secili.has(i) ? '1' : '0.22';
      el.style.cursor = 'pointer';
    });
  });

  const tikla = (e) => {
    const el = e.target.closest('.lp');
    if (!el) return;
    const i = Number(el.style.getPropertyValue('--i'));
    setSecili((s) => { const y = new Set(s); if (y.has(i)) y.delete(i); else y.add(i); return y; });
  };

  const kaydet = async () => {
    setMesgul(true);
    try {
      const c = await markaApi.logoGuncelle(slug, logo.yonelim, { amblem_parcalar: [...secili].sort((a, b) => a - b) });
      message.success('Amblem parçaları kaydedildi');
      onDegisti?.(c); onKapat();
    } catch (e) { message.error(hataMesaji(e), 6); } finally { setMesgul(false); }
  };

  return (
    <Modal open={acik} onCancel={onKapat} width={900} title={`${YONELIM_ADI[logo.yonelim]} — amblem parçaları`}
      onOk={kaydet} okText="Onayla ve kaydet" confirmLoading={mesgul} destroyOnHidden>
      <Paragraph type="secondary">
        Logonun amblem (sembol) olan parçalarına tıklayın: <b>koyu</b> görünenler amblem, <b>soluk</b> olanlar yazı sayılır.
        Portaldaki "Önce amblem" animasyonu bu ayrımı kullanır. Amblemi olmayan (yalnız yazı) logoda hiçbirini seçmeden onaylayın.
        {logo.amblem_oneri?.length > 0 && ' Başlangıç seçimi, markanın amblem logosuyla aynı çizilmiş parçalardan önerildi.'}
      </Paragraph>
      <LogoSvg logo={logo} yukseklik={220} kutuRef={kutu} onClick={tikla} />
      <Space style={{ marginTop: 12 }}>
        <Text>{secili.size} / {logo.parca} parça amblem</Text>
        {logo.amblem_oneri?.length > 0 && <Button size="small" onClick={() => setSecili(new Set(logo.amblem_oneri))}>Öneriye dön</Button>}
        <Button size="small" onClick={() => setSecili(new Set(logo.amblem_parcalar))}>Kayıtlı seçime dön</Button>
        <Button size="small" onClick={() => setSecili(new Set([...Array(logo.parca).keys()]))}>Hepsi</Button>
        <Button size="small" onClick={() => setSecili(new Set())}>Hiçbiri</Button>
      </Space>
    </Modal>
  );
}

/**
 * Bir logo yönelimi: ekran önizlemesi, amblem onayı, not ve 5 varyant × SVG/PDF dosya yuvası.
 * Renkli SVG yüklenince yönelim oluşur (ekran verisi ondan üretilir).
 */
export default function LogoKarti({ slug, yonelim, logo, dosyalar, onDegisti }) {
  const { message } = App.useApp();
  const [amblemAcik, setAmblemAcik] = useState(false);
  const [notMetni, setNotMetni] = useState(logo?.not || '');
  useEffect(() => setNotMetni(logo?.not || ''), [logo]);

  const notKaydet = async () => {
    try { onDegisti?.(await markaApi.logoGuncelle(slug, yonelim, { not_metni: notMetni })); message.success('Not kaydedildi'); }
    catch (e) { message.error(hataMesaji(e), 6); }
  };
  const kaldir = async () => {
    try { onDegisti?.(await markaApi.logoKaldir(slug, yonelim)); message.success('Yönelim kaldırıldı (dosyalar geçmişte)'); }
    catch (e) { message.error(hataMesaji(e), 6); }
  };

  if (!logo) {
    return (
      <Card size="small" title={YONELIM_ADI[yonelim]} type="inner" style={{ opacity: 0.85 }}>
        <Paragraph type="secondary" style={{ marginBottom: 8 }}>
          Bu düzen yok. Eklemek için <b>renkli SVG</b>'yi yükleyin (yazılar eğriye çevrilmiş, gömülü resimsiz vektör).
        </Paragraph>
        <Yuva slug={slug} yuva={`logo/${yonelim}/renkli/svg`} etiket="Renkli SVG" dosya={dosyalar[`logo/${yonelim}/renkli/svg`]} onDegisti={onDegisti} />
      </Card>
    );
  }

  const satirlar = Object.keys(VARYANT_ADI).map((v) => ({ v }));
  return (
    <Card size="small" type="inner"
      title={<Space>{YONELIM_ADI[yonelim]}
        {logo.amblem_onay === false
          ? <Tag color="red">Amblem onayı bekliyor</Tag>
          : <Tag color="green">{logo.amblem_parcalar.length} amblem parçası</Tag>}</Space>}
      extra={<Space>
        <Button size="small" type={logo.amblem_onay === false ? 'primary' : 'default'} onClick={() => setAmblemAcik(true)}>Amblem parçaları</Button>
        <Popconfirm title="Bu yönelim portaldan kaldırılsın mı? Dosyalar geçmişte kalır." onConfirm={kaldir}>
          <Button size="small" danger>Kaldır</Button>
        </Popconfirm>
      </Space>}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) 2fr', gap: 16 }}>
        <div>
          <LogoSvg logo={logo} />
          <Text type="secondary" style={{ fontSize: 12 }}>{logo.parca} parça · oran {logo.oran} · {logo.tarih || ''}</Text>
          <Input.TextArea rows={2} style={{ marginTop: 8 }} placeholder="Not (portalda logonun altında görünür)"
            value={notMetni} onChange={(e) => setNotMetni(e.target.value)} />
          {notMetni !== (logo.not || '') && <Button size="small" style={{ marginTop: 4 }} onClick={notKaydet}>Notu kaydet</Button>}
        </div>
        <Table size="small" pagination={false} rowKey="v" dataSource={satirlar} columns={[
          { title: 'Varyant', dataIndex: 'v', width: 110, render: (v) => VARYANT_ADI[v] },
          { title: 'SVG', render: (_, { v }) => <Yuva kucuk slug={slug} yuva={`logo/${yonelim}/${v}/svg`} dosya={dosyalar[`logo/${yonelim}/${v}/svg`]} onDegisti={onDegisti} /> },
          { title: 'PDF', render: (_, { v }) => <Yuva kucuk slug={slug} yuva={`logo/${yonelim}/${v}/pdf`} dosya={dosyalar[`logo/${yonelim}/${v}/pdf`]} onDegisti={onDegisti} /> },
        ]} />
      </div>
      <AmblemPenceresi slug={slug} logo={logo} acik={amblemAcik} onKapat={() => setAmblemAcik(false)} onDegisti={onDegisti} />
    </Card>
  );
}
